import { httpClient } from "./api";
import { pollUntil } from "../utils/asyncPolling";

// Geração assíncrona do extrato em PDF: um único endpoint que a gente
// re-chama (poll) até ele devolver o PDF em vez de um status intermediário.
// Contrato real, definido pelo backend:
//
// GET product/report/pdf/{callId}/{productId}/
//   {"pdf_status": "pending"}    → ainda não começou, continua pollando
//   {"pdf_status": "processing"} → gerando, continua pollando
//   {"pdf_status": "failed"}     → continua pollando (a próxima chamada
//                                   redispara sozinha, sem ação nossa)
//   {"pdf_status": "ready", "download_url": "..."} → pronto — URL assinada
//
//   403 → consulta não é da empresa do usuário logado
//   404 → call/produto não existe ou não tem extrato habilitado
//   503 → timeout (só em consultas antigas sem cache)

export type ExtratoPdfStatus = "pending" | "processing" | "failed" | "ready";

export type ExtratoPollResult =
  | { ready: true; downloadUrl: string }
  | { ready: false; pdfStatus: ExtratoPdfStatus };

export class ExtratoForbiddenError extends Error {
  constructor(message = "Essa consulta não pertence à sua empresa.") {
    super(message);
    this.name = "ExtratoForbiddenError";
  }
}

export class ExtratoNotFoundError extends Error {
  constructor(message = "Consulta não encontrada.") {
    super(message);
    this.name = "ExtratoNotFoundError";
  }
}

export class ExtratoBackendTimeoutError extends Error {
  constructor(
    message = "Não foi possível carregar essa consulta (sem cache disponível). Tente novamente em instantes."
  ) {
    super(message);
    this.name = "ExtratoBackendTimeoutError";
  }
}

export function getExtratoPollIntervalMs(): number {
  return Number(process.env.REACT_APP_EXTRATO_POLL_INTERVAL_MS ?? 3000);
}

export function getExtratoPollMaxTries(): number {
  return Number(process.env.REACT_APP_EXTRATO_POLL_MAX_TRIES ?? 30);
}

export async function pollExtratoPdfOnce(
  callId: string,
  productId: string
): Promise<ExtratoPollResult> {
  const response = await httpClient.get(
    `product/report/pdf/${callId}/${productId}/`,
    { validateStatus: () => true }
  );

  if (response.status === 403) throw new ExtratoForbiddenError();
  if (response.status === 404) throw new ExtratoNotFoundError();
  if (response.status === 503) throw new ExtratoBackendTimeoutError();

  const body = response.data as { pdf_status: ExtratoPdfStatus; download_url?: string };
  if (body.pdf_status === "ready") {
    if (!body.download_url) {
      throw new Error("A API disse que o extrato está pronto, mas não retornou o download_url.");
    }
    return { ready: true, downloadUrl: body.download_url };
  }

  return { ready: false, pdfStatus: body.pdf_status };
}

// Usada pelo aquecimento em Dashboard/index.tsx: só espera o extrato ficar
// pronto (ou dar erro) antes de navegar, não usa a URL retornada.
export async function waitForExtratoPdfReady(
  callId: string,
  productId: string
): Promise<void> {
  const first = await pollExtratoPdfOnce(callId, productId);
  if (first.ready) return;

  const { promise } = pollUntil<ExtratoPollResult>({
    fetch: () => pollExtratoPdfOnce(callId, productId),
    isDone: (p) => p.ready,
    isError: () => false,
    intervalMs: getExtratoPollIntervalMs(),
    maxAttempts: getExtratoPollMaxTries(),
  });

  await promise;
}
